/**
 * @krisspy-file
 * @type component
 * @name "AddIdeaModal"
 * @description "Modální okno pro přidání vlastního nápadu s informací o odeslání ke schválení"
 */

import React, { useState, useEffect } from 'react';
import { X, Lightbulb, Send } from 'lucide-react';

interface AddIdeaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (idea: { title: string; description: string }) => void;
  touchpointId?: string;
  touchpointName?: string;
}

const AddIdeaModal: React.FC<AddIdeaModalProps> = ({
  isOpen,
  onClose,
  onAdd,
  touchpointId,
  touchpointName,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<{ title?: string; description?: string }>({});

  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setDescription('');
      setErrors({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const validate = () => {
    const e: { title?: string; description?: string } = {};
    if (!title.trim()) e.title = 'Název nápadu je povinný';
    return e;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    onAdd({ title: title.trim(), description: description.trim() });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-amber-50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center">
              <Lightbulb className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Přidat nápad</h2>
              {touchpointName && (
                <p className="text-xs text-gray-400 mt-0.5">Pro: {touchpointName}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 rounded-lg p-1 hover:bg-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Info box */}
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
            <Lightbulb className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-amber-500" />
            <span>
              Váš nápad bude odeslán adminovi jako námět ke schválení.
            </span>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Název nápadu *
            </label>
            <input
              type="text"
              value={title}
              onChange={e => { setTitle(e.target.value); setErrors(prev => ({ ...prev, title: undefined })); }}
              autoFocus
              placeholder="např. Zlepšit onboarding zákazníků"
              className={`w-full px-3 py-2.5 text-sm border rounded-lg outline-none transition-all placeholder-gray-400
                ${errors.title ? 'border-red-400 focus:ring-2 focus:ring-red-300' : 'border-gray-300 focus:ring-2 focus:ring-amber-400 focus:border-transparent'}`}
            />
            {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title}</p>}
          </div>

          {/* Description (optional) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Popis <span className="text-gray-400 font-normal">(volitelné)</span>
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Popište váš nápad podrobněji..."
              rows={3}
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all placeholder-gray-400 resize-none"
            />
          </div>

          {/* Footer */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 px-4 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
            >
              Zrušit
            </button>
            <button
              type="submit"
              className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 rounded-xl shadow-sm transition-colors"
            >
              <Send className="w-4 h-4" />
              Odeslat nápad
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddIdeaModal;
